// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { chargeSchema } from '@/billing/api/types';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

const setChargeBillableStateResponseSchema = z
  .object({
    data: chargeSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, charge: data }));

type SetChargeBillableStateResponse = z.infer<
  typeof setChargeBillableStateResponseSchema
>;

// Optional body carried on the mark-not-billable path. When
// expectedBillableDate is supplied, the backend overwrites the charge's
// expected_billable_date and emits an audit delta alongside the status flip.
// When omitted, the existing schedule is kept.
interface MarkNotBillableBody {
  expectedBillableDate?: DateTime;
}

export async function setChargeBillableState(
  chargeKey: string,
  markBillable: boolean,
  body?: MarkNotBillableBody
): Promise<SetChargeBillableStateResponse> {
  const action = markBillable ? 'mark-billable' : 'mark-not-billable';
  const url = `/v1/charge/${chargeKey}/${action}`;
  const response = await api.post(
    url,
    body === undefined
      ? null
      : { expectedBillableDate: serializeDateForAPI(body.expectedBillableDate) }
  );

  return parseResponse(setChargeBillableStateResponseSchema, response.data, {
    schemaName: markBillable ? 'MarkChargeBillable' : 'MarkChargeNotBillable',
    endpoint: url,
  });
}
